import React, { useState, useCallback, createContext, useContext } from 'react';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcuts';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import CommandPalette from './CommandPalette';

const KeyboardShortcutsContext = createContext({
  showShortcutsModal: () => {},
  hideShortcutsModal: () => {},
  isShortcutsModalOpen: false,
  showCommandPalette: () => {},
  hideCommandPalette: () => {},
  isCommandPaletteOpen: false,
});

export function useShortcutsModal() {
  return useContext(KeyboardShortcutsContext);
}

export function useCommandPalette() {
  return useContext(KeyboardShortcutsContext);
}

/**
 * Provider that handles global keyboard shortcuts:
 * - `?` → Opens the shortcuts cheat sheet
 * - `Cmd+K` → Opens the command palette
 * - `Escape` → Closes open modals (handled inside each modal)
 *
 * Place this inside <Router> but wrapping <AppContent> so it can
 * access routing context and render above all page content.
 */
export default function KeyboardShortcutsProvider({ children }) {
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  const showShortcutsModal = useCallback(() => setIsShortcutsOpen(true), []);
  const hideShortcutsModal = useCallback(() => setIsShortcutsOpen(false), []);
  const showCommandPalette = useCallback(() => setIsCommandPaletteOpen(true), []);
  const hideCommandPalette = useCallback(() => setIsCommandPaletteOpen(false), []);

  // `?` opens the shortcuts cheat sheet
  useKeyboardShortcut(
    '?',
    () => {
      if (!isCommandPaletteOpen) {
        setIsShortcutsOpen((prev) => !prev);
      }
    },
    { mod: false, shift: false, allowInInput: false },
    [isCommandPaletteOpen]
  );

  // `Cmd+K` opens the command palette
  useKeyboardShortcut(
    'k',
    () => {
      // Close shortcuts modal if open
      setIsShortcutsOpen(false);
      setIsCommandPaletteOpen((prev) => !prev);
    },
    { mod: true, allowInInput: true },
    []
  );

  return (
    <KeyboardShortcutsContext.Provider
      value={{
        showShortcutsModal,
        hideShortcutsModal,
        isShortcutsModalOpen: isShortcutsOpen,
        showCommandPalette,
        hideCommandPalette,
        isCommandPaletteOpen,
      }}
    >
      {children}
      <KeyboardShortcutsModal isOpen={isShortcutsOpen} onClose={hideShortcutsModal} />
      <CommandPalette isOpen={isCommandPaletteOpen} onClose={hideCommandPalette} />
    </KeyboardShortcutsContext.Provider>
  );
}
